import type { Meter } from "@eisox/api/core-api";
import { Alert, Button, cn, TableV2 } from "@eisox/design-system";
import { GaugeIcon, QuestionMarkIcon } from "@eisox/icons";
import { useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { KpiGrid } from "~/components/kpi-grid";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

import { GRANULARITIES, UNALLOCATED_KEY } from "../consumption.constant";
import { hierarchyQueries } from "../consumption.queries";
import { energyIcon, formatConsumption, formatPercent, unitOf } from "../helpers/energy.helper";
import { formatBucket } from "../helpers/period.helper";
import { breakdown, breakdownAlert, breakdownRows, hasConsumption } from "../helpers/series.helper";
import { BreakdownAlert, ConsumptionTab, type Breakdown, type ConsumptionPeriod } from "../types/consumption.type";
import { ConsumptionChart } from "./consumption-chart";

const NAME = "SubMeterBreakdown";

const ALERT_MESSAGES: Record<BreakdownAlert, (meterBreakdown: Breakdown, unit?: string) => string> = {
  [BreakdownAlert.SUB_METERS_EXCEED]: ({ unallocated }, unit) =>
    m.consumption_sub_meters_exceed_alert({ value: formatConsumption(-unallocated, unit) }),
  [BreakdownAlert.HIGH_UNALLOCATED]: ({ unallocatedShare }) =>
    m.consumption_unallocated_alert({ share: formatPercent(unallocatedShare) }),
};

interface SubMeterBreakdownProps {
  meter: Meter;
  subMeters: Meter[];
  period: ConsumptionPeriod;
  onBucketOpen: (meterId: string, start: string) => void;
}

const SubMeterBreakdown: React.FC<SubMeterBreakdownProps> = ({ meter, subMeters, period, onBucketOpen }) => {
  const { building } = useBuildingContext(NAME);
  const queries = hierarchyQueries(
    building,
    meter.id,
    subMeters.map(({ id }) => id),
    period,
  );
  const {
    data: { buckets: meterBuckets },
  } = useSuspenseQuery(queries.meter);
  const subMeterResults = useSuspenseQueries({ queries: queries.subMeters });

  if (!hasConsumption(meterBuckets)) {
    return <p className="text-primary-dark-gray">{m.no_result()}</p>;
  }

  const subMeterConsumptions = subMeters.map((subMeter, position) => ({
    meter: subMeter,
    buckets: subMeterResults[position]?.data.buckets ?? [],
  }));
  const meterBreakdown = breakdown(meterBuckets, subMeterConsumptions);
  const warning = breakdownAlert(meterBreakdown);
  const unit = unitOf(meter);
  const { bucketFormat, tickFormat } = GRANULARITIES[period.granularity];

  return (
    <div className="gap-2.5 md:gap-5 min-h-0 flex flex-1 flex-col">
      <KpiGrid
        kpis={[
          {
            Icon: energyIcon(meter),
            value: formatConsumption(meterBreakdown.total, unit),
            label: m.consumption_main_meter(),
          },
          {
            Icon: GaugeIcon,
            value: formatConsumption(meterBreakdown.subMetersTotal, unit),
            label: m.consumption_sub_meters(),
          },
          {
            Icon: QuestionMarkIcon,
            value: `${formatConsumption(meterBreakdown.unallocated, unit)} · ${formatPercent(meterBreakdown.unallocatedShare)}`,
            label: m.consumption_unallocated(),
          },
        ]}
      />
      {warning && <Alert severity="warning">{ALERT_MESSAGES[warning](meterBreakdown, unit)}</Alert>}
      <ConsumptionChart
        rows={hiddenSeries =>
          breakdownRows(
            meterBuckets,
            subMeterConsumptions.filter(({ meter: subMeter }) => !hiddenSeries.includes(subMeter.id)),
            { granularity: period.granularity, timezone: building.timezone },
          )
        }
        series={meterBreakdown.segments}
        unit={unit}
        formatTick={start => formatBucket(start, tickFormat, building.timezone)}
        formatValue={value => formatConsumption(value, unit)}
        formatTooltipTitle={start => formatBucket(start, bucketFormat, building.timezone)}
        onPointSelect={(start, series) => onBucketOpen(series === UNALLOCATED_KEY ? meter.id : series, start)}
      />
      <TableV2.Container className="max-h-56 shrink-0 overflow-y-auto">
        <TableV2.Root className="table-fixed">
          <colgroup>
            <col className="w-7.5" />
            <col />
            <col className="md:w-40 w-28" />
            <col className="md:w-28 w-16" />
          </colgroup>
          <TableV2.Header className="top-0 sticky z-1 border-b-2 border-primary-darker-gray bg-primary-white">
            <TableV2.Row>
              <TableV2.Head />
              <TableV2.Head className="pb-2.5 text-left">{m.consumption_sub_meter()}</TableV2.Head>
              <TableV2.Head className="pb-2.5 text-left">{m.consumption_total()}</TableV2.Head>
              <TableV2.Head className="pb-2.5 text-left">{m.consumption_share()}</TableV2.Head>
            </TableV2.Row>
          </TableV2.Header>
          <TableV2.Body>
            {meterBreakdown.segments.map(({ key, label, color, total, share }) => (
              <TableV2.Row
                key={key}
                className={cn(
                  "border-b border-primary-gray last:border-0",
                  key !== UNALLOCATED_KEY && "relative hover:bg-primary-gray",
                )}
              >
                <TableV2.Cell className="py-2.5">
                  <span className="size-2.5 block rounded-full" style={{ backgroundColor: color }} />
                </TableV2.Cell>
                <TableV2.Cell className="py-2.5">
                  {key === UNALLOCATED_KEY ? (
                    label
                  ) : (
                    <Button asChild>
                      <Link
                        to="/buildings/$buildingId/consumption/$meterId"
                        params={{ buildingId: building.id, meterId: key }}
                        search={previous => ({ ...previous, tab: ConsumptionTab.CHART })}
                        className="after:inset-0 after:absolute"
                      >
                        {label}
                      </Link>
                    </Button>
                  )}
                </TableV2.Cell>
                <TableV2.Cell className="py-2.5 tabular-nums">{formatConsumption(total, unit)}</TableV2.Cell>
                <TableV2.Cell className="py-2.5 tabular-nums">{formatPercent(share)}</TableV2.Cell>
              </TableV2.Row>
            ))}
          </TableV2.Body>
        </TableV2.Root>
      </TableV2.Container>
    </div>
  );
};

SubMeterBreakdown.displayName = NAME;

export { SubMeterBreakdown };
